'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive Combobox
 * ROLE: combobox | ARIA: aria-haspopup, aria-expanded, aria-controls, aria-autocomplete=list, aria-activedescendant
 * KEYBOARD: Down/Up nav; Enter commits active; Esc closes/clears query; Home/End jump; Backspace clears selection from rest
 * FOCUS: input keeps focus across open/close; popover content does not auto-trap
 * PRM: animation kept minimal; reduced-motion respects via CSS
 * FORCED-COLORS: panel border = ButtonText; active option uses Highlight
 * RTL: side/align flips via Radix's logic
 * RSC: CLIENT
 * WAI-APG: https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
 * SUBSTRATE: @radix-ui/react-popover (Trigger asChild on the input)
 *
 * Single-select combobox over a static option list. Filters by case-insensitive
 * substring of `label` (and optional `keywords`). When the consumer's source
 * data is large or remote, pre-filter before passing options — the primitive
 * does no async fetching itself.
 *
 * The input is rendered as `Popover.Trigger asChild`, which:
 *   - registers the input in Radix's `DismissableLayer` interactive zone, so
 *     clicks/focus on it don't dismiss the popover and the popover composes
 *     correctly inside Dialog (the listbox layer stacks above the dialog),
 *   - lets Radix wire `aria-haspopup`, `aria-expanded`, `aria-controls` and
 *     manage the open/close on input click (same UX as a native `<select>`),
 *   - keeps positioning via Popover's built-in Popper anchor, with collision
 *     detection (flips above when the anchor is near the viewport bottom).
 *
 * Typeahead behaviour: while the popover is closed and the user starts
 * typing, the panel opens so they can see matches. Standard combobox feel.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { useObjectRef } from 'react-aria';
import * as Popover from '@radix-ui/react-popover';
import { X, ChevronDown, Search } from 'lucide-react';
import { useFieldContext } from '../forms/Field/FieldContext';
import styles from './Combobox.module.css';
function defaultMatch(opt, q) {
    if (q === '')
        return true;
    const lower = q.toLowerCase();
    if (opt.label.toLowerCase().includes(lower))
        return true;
    if (opt.keywords !== undefined && opt.keywords.toLowerCase().includes(lower))
        return true;
    return false;
}
export function Combobox({ options, value, onChange, placeholder = 'Search…', ariaLabel, emptyLabel = 'No matches', noOptionsLabel, renderOption, disabled = false, maxPanelHeight = 280, invalid = false, id, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid = false, 'aria-required': ariaRequired = false, onQueryChange, noInternalFilter = false, allowFreeText = false, loading = false, loadingLabel = 'Searching…', error = false, errorLabel, search = false, ref, }) {
    const generatedId = useId();
    const rootId = id ?? generatedId;
    const listboxId = `${rootId}-listbox`;
    // Inside a Field whose label points at this input, that visible label is the
    // name; an aria-label would override it for screen readers.
    const field = useFieldContext();
    const fieldLabelId = field?.labelFor === rootId ? field.labelledBy : undefined;
    const ownLabel = fieldLabelId === undefined ? ariaLabel : undefined;
    const inputRef = useObjectRef(ref);
    const listRef = useRef(null);
    const selected = useMemo(() => {
        if (value === null)
            return null;
        const found = options.find((o) => o.value === value);
        if (found)
            return found;
        // allowFreeText: value may not be in options (free-typed text) — synthesise a display option.
        if (allowFreeText)
            return { value, label: value };
        return null;
    }, [options, value, allowFreeText]);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    // Flips true when `commit(value)` fires, signalling that the next
    // open→close transition was an option commit (not a free-text close).
    // Without this, `allowFreeText` would re-fire `onChange(query)` on close
    // after the user explicitly selected an option — a duplicate commit.
    const justCommittedRef = useRef(false);
    const filtered = useMemo(() => {
        if (noInternalFilter)
            return options;
        const q = query.trim();
        if (q === '')
            return options;
        return options.filter((opt) => defaultMatch(opt, q));
    }, [options, query, noInternalFilter]);
    // Keep activeIdx in range as the filtered list shrinks/grows.
    useEffect(() => {
        if (activeIdx > filtered.length - 1) {
            setActiveIdx(filtered.length === 0 ? 0 : filtered.length - 1);
        }
    }, [filtered.length, activeIdx]);
    // Scroll the active option into view when it changes.
    useEffect(() => {
        if (!open)
            return;
        const node = listRef.current?.querySelector(`[data-cb-idx="${activeIdx}"]`);
        node?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIdx]);
    // When the panel closes, commit the typed query as a free-text value (if
    // allowFreeText is on, the close wasn't an option-commit, and the query
    // is non-empty and differs from the current selection), then clear the
    // query input unconditionally.
    useEffect(() => {
        if (!open) {
            if (allowFreeText && !justCommittedRef.current) {
                const trimmed = query.trim();
                if (trimmed !== '' && trimmed !== selected?.value) {
                    onChange(trimmed);
                }
            }
            justCommittedRef.current = false;
            setQuery('');
        }
        // `query` and `selected` are intentionally omitted — this effect must only
        // run on open→close transitions; reading query/selected via closure is safe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const commit = useCallback((next) => {
        justCommittedRef.current = true;
        onChange(next);
        setOpen(false);
    }, [onChange]);
    const clear = useCallback(() => {
        onChange(null);
        setOpen(false);
        inputRef.current?.focus();
    }, [onChange]);
    const onInputChange = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        setActiveIdx(0);
        onQueryChange?.(e.target.value);
    };
    const onKeyDown = (e) => {
        if (disabled)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                setActiveIdx(0);
                return;
            }
            setActiveIdx((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                return;
            }
            setActiveIdx((i) => Math.max(i - 1, 0));
        }
        else if (e.key === 'Home') {
            if (!open)
                return;
            e.preventDefault();
            setActiveIdx(0);
        }
        else if (e.key === 'End') {
            if (!open)
                return;
            e.preventDefault();
            setActiveIdx(Math.max(filtered.length - 1, 0));
        }
        else if (e.key === 'Enter') {
            const opt = filtered[activeIdx];
            if (open && opt !== undefined && opt.disabled !== true) {
                e.preventDefault();
                commit(opt.value);
            }
        }
        else if (e.key === 'Escape') {
            // Popover's own Esc handler will also fire and close the panel via
            // onOpenChange — we additionally clear a stale selection when the
            // panel was already closed (matches Apple's combobox pattern).
            if (!open && selected !== null) {
                e.preventDefault();
                clear();
            }
        }
        else if (e.key === 'Backspace' && query === '' && selected !== null && !open) {
            // From the resting state, Backspace clears the selection — matches the
            // standard browser <select multiple> behavior and Apple's pickers.
            e.preventDefault();
            clear();
        }
    };
    const inputDisplayValue = open ? query : (selected?.label ?? '');
    const showClearBtn = selected !== null && !open;
    const activeOptionId = open && filtered[activeIdx]
        ? `${rootId}-opt-${activeIdx}`
        : undefined;
    const inputClass = `${styles.input}${invalid ? ` ${styles['input--invalid']}` : ''}${search ? ` ${styles['input--search']}` : ''}`;
    return (_jsx(Popover.Root, { open: open, onOpenChange: setOpen, children: _jsxs("div", { className: styles.root, "data-state": open ? 'open' : 'closed', children: [_jsxs("div", { className: styles.inputWrap, children: [search ? (_jsx("span", { className: styles.searchIcon, "aria-hidden": "true", children: _jsx(Search, {}) })) : null, _jsx(Popover.Trigger, { asChild: true, children: _jsx("input", { ref: inputRef, id: rootId, type: "text", role: "combobox", autoComplete: "off", "aria-label": ownLabel, "aria-autocomplete": "list", "aria-activedescendant": activeOptionId, "aria-describedby": ariaDescribedBy, "aria-invalid": invalid || ariaInvalid || undefined, "aria-required": ariaRequired || undefined, placeholder: selected !== null && !open ? selected.label : placeholder, value: inputDisplayValue, onChange: onInputChange, onKeyDown: onKeyDown, disabled: disabled, className: inputClass, "data-1p-ignore": "true", "data-lpignore": "true", "data-bwignore": "true" }) }), showClearBtn ? (_jsx("button", { type: "button", "aria-label": "Clear selection", onClick: clear, onMouseDown: (e) => e.preventDefault(), className: styles.trailingBtn, tabIndex: -1, children: _jsx(X, { "aria-hidden": "true", focusable: "false" }) })) : search ? null : (_jsx("span", { className: styles.chevron, "aria-hidden": "true", children: _jsx(ChevronDown, {}) }))] }), _jsx(Popover.Portal, { children: _jsx(Popover.Content, { ref: listRef, role: "listbox", id: listboxId, "aria-label": ownLabel, "aria-labelledby": fieldLabelId, className: styles.panel, style: { maxHeight: maxPanelHeight }, side: "bottom", align: "start", sideOffset: 4, collisionPadding: 8, 
                        // Keep keyboard focus on the input; Radix's default is to move
                        // focus into the content, which would break combobox semantics
                        // (input must continue receiving keystrokes for typeahead).
                        onOpenAutoFocus: (e) => e.preventDefault(), 
                        // Don't snap focus back to the trigger on close — we either
                        // stay on the input (after commit) or the user has tabbed away.
                        onCloseAutoFocus: (e) => e.preventDefault(), children: loading && filtered.length === 0 ? (_jsx("div", { className: styles.empty, children: loadingLabel })) : filtered.length === 0 ? (_jsx("div", { className: styles.empty, children: error && errorLabel !== undefined
                                ? errorLabel
                                : // "Nothing to match against yet" → noOptionsLabel. For async
                                    // (noInternalFilter) consumers `options` mirrors the query
                                    // result, so an empty `options` can't tell "not typed yet"
                                    // from "query matched nothing" — gate on the live query
                                    // instead. Internal-filter consumers keep the source-empty
                                    // meaning so their loading/none copy is unaffected.
                                    (noInternalFilter ? query.trim() === '' : options.length === 0) &&
                                        noOptionsLabel !== undefined
                                        ? noOptionsLabel
                                        : emptyLabel })) : (filtered.map((opt, idx) => {
                            const active = idx === activeIdx;
                            const isSelected = selected?.value === opt.value;
                            const isDisabled = opt.disabled === true;
                            return (_jsx("div", { id: `${rootId}-opt-${idx}`, "data-cb-idx": idx, role: "option", "aria-selected": isSelected, "aria-disabled": isDisabled || undefined, className: `${styles.option} ${isDisabled ? styles['option--disabled'] : active ? styles['option--active'] : ''}`, onMouseEnter: () => {
                                    if (!isDisabled)
                                        setActiveIdx(idx);
                                }, onMouseDown: (e) => {
                                    // mousedown so the input doesn't blur first and close
                                    // the panel before our commit runs.
                                    e.preventDefault();
                                    if (isDisabled)
                                        return;
                                    commit(opt.value);
                                }, children: renderOption ? (renderOption(opt, { active })) : (_jsxs("div", { className: styles.optionBody, children: [_jsx("span", { className: styles.optionLabel, children: opt.label }), opt.sublabel !== undefined ? (_jsx("span", { className: styles.optionSublabel, children: opt.sublabel })) : null] })) }, opt.value));
                        })) }) })] }) }));
}
//# sourceMappingURL=Combobox.js.map